import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import { IScheduleStatus } from '../../shared/interfaces';
import ScheduleStatusCreateEdit from './CreateEdit';
import { ColorContainer } from './styles';
import { getSchedulesStatus } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const SchedulesStatus: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [scheduleStatus, setScheduleStatus] = useState<IScheduleStatus | null>(
    null,
  );
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [schedulesStatus, setSchedulesStatus] = useState<IScheduleStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setSchedulesStatus([]);
    try {
      const response = await getSchedulesStatus(
        limit,
        (page - 1) * limit,
        search,
      );
      setPages(response.count);
      setSchedulesStatus(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar status',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalScheduleStatusCreateEdit = () => {
    if (modalCreate)
      return (
        <ScheduleStatusCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          scheduleStatus={scheduleStatus}
          setSchedulesStatus={setSchedulesStatus}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<UnorderedListOutlined />}
        title="Status da agenda"
        description="Gerencie os status visuais da agenda com cores e descrições padronizadas para facilitar a operação diária."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.schedulesStatusCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setScheduleStatus(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Cor',
              dataIndex: 'color',
              render: (value: string) => <ColorContainer color={value} />,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IScheduleStatus) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.schedulesStatusUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setScheduleStatus(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={schedulesStatus}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalScheduleStatusCreateEdit()}
    </PageLayout>
  );
};

export default SchedulesStatus;
